import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  createLevel,
  deleteLevel,
  getChallenges,
  getBadges,
  getProgressionLevels,
  updateLevel,
  type Challenge,
  type Badge,
  type LevelRequirement,
} from '../services/progressionAdminApi';

const DOC_DOMAINS = ['ST', 'ACC', 'MD', 'M', 'SONG', 'EX', 'Other'];

type PathDocRow = { documentCode: string; domain: string; minProgression: number; order: number };

type PathForm = { pathName: string; description: string; documents: PathDocRow[] };

const emptyPath = (): PathForm => ({ pathName: '', description: '', documents: [] });
const emptyDoc = (order = 0): PathDocRow => ({ documentCode: '', domain: 'ST', minProgression: 75, order });

const PATH_MODES = ['either', 'pathA', 'pathB', 'both'] as const;

const emptyForm = () => ({
  level: '1',
  levelName: '',
  description: '',
  pathA: emptyPath(),
  pathB: emptyPath(),
  minTotalExperience: '',
  pathCompletionMode: 'either' as (typeof PATH_MODES)[number],
  levelCompletionBonusXP: '',
  levelRewardBadgeIds: [] as string[],
  requiredBadgeIds: [] as string[],
  requiredGroupBadgeIds: [] as string[],
  requiredChallengeIds: [] as string[],
});

function pathDocCount(level: LevelRequirement, key: 'pathA' | 'pathB') {
  return level[key]?.documents?.length ?? 0;
}

function pathFromLevel(path?: LevelRequirement['pathA']): PathForm {
  if (!path) return emptyPath();
  return {
    pathName: path.pathName || '',
    description: path.description || '',
    documents: (path.documents || []).map((d, i) => ({
      documentCode: d.documentCode || '',
      domain: d.documentDomain || 'ST',
      minProgression: d.minProgression ?? 75,
      order: d.order ?? i,
    })),
  };
}

function buildPathPayload(path: PathForm) {
  const docs = path.documents.filter((d) => d.documentCode.trim());
  return {
    pathName: path.pathName.trim() || undefined,
    description: path.description.trim() || undefined,
    documents: docs.map((d, i) => ({
      documentCode: d.documentCode.trim(),
      documentDomain: d.domain,
      minProgression: d.minProgression,
      order: d.order ?? i,
    })),
  };
}

const PATH_MODE_LABELS: Record<(typeof PATH_MODES)[number], string> = {
  either: 'Either path A or path B',
  pathA: 'Path A only',
  pathB: 'Path B only',
  both: 'Both paths required',
};

function nextLevelNumber(levels: LevelRequirement[]): number {
  if (!levels.length) return 1;
  return Math.max(...levels.map((l) => l.level)) + 1;
}

const MultiSelectField: React.FC<{
  label: string;
  emptyHint: string;
  options: Array<{ id: string; name: string }>;
  selectedIds: string[];
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
}> = ({ label, emptyHint, options, selectedIds, onAdd, onRemove }) => (
  <Box>
    <TextField
      select
      fullWidth
      label={label}
      value=""
      onChange={(e) => {
        const id = e.target.value;
        if (id) onAdd(id);
      }}
      helperText={selectedIds.length ? undefined : emptyHint}
    >
      <MenuItem value="" disabled>
        Add…
      </MenuItem>
      {options
        .filter((o) => !selectedIds.includes(o.id))
        .map((o) => (
          <MenuItem key={o.id} value={o.id}>
            {o.name}
          </MenuItem>
        ))}
    </TextField>
    {selectedIds.length > 0 ? (
      <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
        {selectedIds.map((id) => {
          const name = options.find((o) => o.id === id)?.name || id;
          return (
            <Chip key={id} label={name} size="small" onDelete={() => onRemove(id)} />
          );
        })}
      </Box>
    ) : null}
  </Box>
);

const PathEditor: React.FC<{
  title: string;
  path: PathForm;
  onChange: (path: PathForm) => void;
}> = ({ title, path, onChange }) => (
  <Box>
    <Typography variant="subtitle2" gutterBottom>{title}</Typography>
    <TextField
      size="small"
      label="Path name"
      value={path.pathName}
      onChange={(e) => onChange({ ...path, pathName: e.target.value })}
      fullWidth
      sx={{ mb: 1 }}
    />
    <TextField
      size="small"
      label="Description"
      value={path.description}
      onChange={(e) => onChange({ ...path, description: e.target.value })}
      fullWidth
      multiline
      sx={{ mb: 1 }}
    />
    {path.documents.map((d, idx) => (
      <Box key={idx} display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={1}>
        <TextField
          size="small"
          label="Document code"
          value={d.documentCode}
          onChange={(e) => {
            const docs = [...path.documents];
            docs[idx] = { ...docs[idx], documentCode: e.target.value };
            onChange({ ...path, documents: docs });
          }}
          sx={{ flex: 2, minWidth: 160 }}
        />
        <TextField
          select
          size="small"
          label="Domain"
          value={d.domain}
          onChange={(e) => {
            const docs = [...path.documents];
            docs[idx] = { ...docs[idx], domain: e.target.value };
            onChange({ ...path, documents: docs });
          }}
          sx={{ minWidth: 100 }}
        >
          {DOC_DOMAINS.map((v) => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Min %"
          type="number"
          value={d.minProgression}
          onChange={(e) => {
            const docs = [...path.documents];
            docs[idx] = { ...docs[idx], minProgression: parseInt(e.target.value, 10) || 0 };
            onChange({ ...path, documents: docs });
          }}
          sx={{ width: 80 }}
        />
        <IconButton
          size="small"
          color="error"
          onClick={() => onChange({ ...path, documents: path.documents.filter((_, i) => i !== idx) })}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    ))}
    <Button
      size="small"
      onClick={() => onChange({ ...path, documents: [...path.documents, emptyDoc(path.documents.length)] })}
    >
      Add document
    </Button>
  </Box>
);

type LevelAdminTabProps = {
  onMessage: (msg: string) => void;
};

export const LevelAdminTab: React.FC<LevelAdminTabProps> = ({ onMessage }) => {
  const [levels, setLevels] = useState<LevelRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ open: boolean; editing?: LevelRequirement }>({ open: false });
  const [form, setForm] = useState(emptyForm());
  const [badges, setBadges] = useState<Badge[]>([]);
  const [challenges, setChallenges] = useState<Challenge[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [levelsRes, badgesRes, challengesRes] = await Promise.all([
        getProgressionLevels(),
        getBadges(),
        getChallenges({ isActive: true }),
      ]);
      setLevels(levelsRes.data?.levels || []);
      setBadges(badgesRes.data?.badges || []);
      setChallenges(challengesRes.data?.challenges || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load levels');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (l?: LevelRequirement) => {
    if (!l) {
      setForm({ ...emptyForm(), level: String(nextLevelNumber(levels)) });
      setDialog({ open: true });
      return;
    }
    const adv = l.advancementRequirements;
    setForm({
      level: String(l.level),
      levelName: l.levelName || '',
      description: l.description || '',
      pathA: pathFromLevel(l.pathA),
      pathB: pathFromLevel(l.pathB),
      minTotalExperience: adv?.minTotalExperience != null ? String(adv.minTotalExperience) : '',
      pathCompletionMode: adv?.pathCompletion?.mode || 'either',
      levelCompletionBonusXP: adv?.levelCompletionBonusXP != null ? String(adv.levelCompletionBonusXP) : '',
      levelRewardBadgeIds: (adv?.levelRewardBadgeIds || []).map(String),
      requiredBadgeIds: (adv?.requiredBadgeIds || []).map(String),
      requiredGroupBadgeIds: (adv?.requiredGroupBadgeIds || []).map(String),
      requiredChallengeIds: (adv?.requiredChallengeIds || []).map(String),
    });
    setDialog({ open: true, editing: l });
  };

  const handleSave = async () => {
    const body = {
      level: parseInt(form.level, 10),
      levelName: form.levelName,
      description: form.description,
      pathA: buildPathPayload(form.pathA),
      pathB: buildPathPayload(form.pathB),
      advancementRequirements: {
        ...(form.minTotalExperience !== '' && { minTotalExperience: parseInt(form.minTotalExperience, 10) }),
        pathCompletion: { mode: form.pathCompletionMode },
        requiredBadgeIds: form.requiredBadgeIds,
        requiredGroupBadgeIds: form.requiredGroupBadgeIds,
        requiredChallengeIds: form.requiredChallengeIds,
        ...(form.levelCompletionBonusXP !== '' && { levelCompletionBonusXP: parseInt(form.levelCompletionBonusXP, 10) }),
        levelRewardBadgeIds: form.levelRewardBadgeIds,
      },
    };

    try {
      if (dialog.editing) {
        await updateLevel(dialog.editing._id, body);
        onMessage('Level updated.');
      } else {
        await createLevel(body);
        onMessage('Level created.');
      }
      setDialog({ open: false });
      await load();
    } catch (e: any) {
      onMessage(e?.message || 'Level save failed');
    }
  };

  if (loading) {
    return <Typography color="text.secondary">Loading levels…</Typography>;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Button size="small" startIcon={<AddIcon />} onClick={() => openEdit()}>Add level</Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{'color':'primary.main'}}>Level</TableCell>
            <TableCell sx={{'color':'primary.main'}}>Name</TableCell>
            <TableCell sx={{'color':'primary.main'}}>Path A docs</TableCell>
            <TableCell sx={{'color':'primary.main'}}>Path B docs</TableCell>
            <TableCell align="right" sx={{'color':'primary.main'}}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {levels.map((l) => (
            <TableRow key={l._id}>
              <TableCell>{l.level}</TableCell>
              <TableCell>{l.levelName || '—'}</TableCell>
              <TableCell>{pathDocCount(l, 'pathA')}</TableCell>
              <TableCell>{pathDocCount(l, 'pathB')}</TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => openEdit(l)}><EditIcon fontSize="small" /></IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={async () => {
                    if (!window.confirm('Delete level?')) return;
                    await deleteLevel(l._id);
                    onMessage('Level deleted.');
                    await load();
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={dialog.open} onClose={() => setDialog({ open: false })} maxWidth="md" fullWidth>
        <DialogTitle>{dialog.editing ? 'Edit level' : 'Create level'}</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh' }}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                Level identity
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  fullWidth
                  label="Level number"
                  type="number"
                  required
                  inputProps={{ min: 1 }}
                  value={form.level}
                  onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))}
                  helperText="Numeric level shown to students (stars)"
                />
                <TextField
                  fullWidth
                  label="Level title"
                  value={form.levelName}
                  onChange={(e) => setForm((f) => ({ ...f, levelName: e.target.value }))}
                  helperText="Display name on My Stage (e.g. Rhythm Explorer)"
                  placeholder="Optional title"
                />
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  minRows={2}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <PathEditor title="Path A" path={form.pathA} onChange={(pathA) => setForm((f) => ({ ...f, pathA }))} />
              <Divider sx={{ my: 2 }} />
              <PathEditor title="Path B" path={form.pathB} onChange={(pathB) => setForm((f) => ({ ...f, pathB }))} />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                Advancement conditions
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                All conditions must be met before the student can advance to the next level.
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  fullWidth
                  label="Minimum total XP"
                  type="number"
                  value={form.minTotalExperience}
                  onChange={(e) => setForm((f) => ({ ...f, minTotalExperience: e.target.value }))}
                  helperText="Optional — leave empty for no XP gate"
                />
                <TextField
                  select
                  fullWidth
                  label="Path completion mode"
                  value={form.pathCompletionMode}
                  onChange={(e) => setForm((f) => ({ ...f, pathCompletionMode: e.target.value as typeof f.pathCompletionMode }))}
                >
                  {PATH_MODES.map((m) => (
                    <MenuItem key={m} value={m}>{PATH_MODE_LABELS[m]}</MenuItem>
                  ))}
                </TextField>
                <MultiSelectField
                  label="Required badges (prerequisites)"
                  emptyHint="Must already hold these badges before advancing"
                  options={badges.map((b) => ({ id: b._id, name: b.name }))}
                  selectedIds={form.requiredBadgeIds}
                  onAdd={(id) => setForm((f) => ({ ...f, requiredBadgeIds: [...f.requiredBadgeIds, id] }))}
                  onRemove={(id) => setForm((f) => ({ ...f, requiredBadgeIds: f.requiredBadgeIds.filter((x) => x !== id) }))}
                />
                <MultiSelectField
                  label="Required challenges"
                  emptyHint="Challenges auto-complete when their conditions are met"
                  options={challenges.map((c) => ({ id: c._id, name: c.name }))}
                  selectedIds={form.requiredChallengeIds}
                  onAdd={(id) => setForm((f) => ({ ...f, requiredChallengeIds: [...f.requiredChallengeIds, id] }))}
                  onRemove={(id) => setForm((f) => ({ ...f, requiredChallengeIds: f.requiredChallengeIds.filter((x) => x !== id) }))}
                />
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                Level-up rewards
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Granted when the student advances past this level. The new title comes from the next level&apos;s config.
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  fullWidth
                  label="Bonus XP on level-up"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={form.levelCompletionBonusXP}
                  onChange={(e) => setForm((f) => ({ ...f, levelCompletionBonusXP: e.target.value }))}
                />
                <MultiSelectField
                  label="Reward badges on level-up"
                  emptyHint="Badges granted when the student promotes past this level"
                  options={badges.map((b) => ({ id: b._id, name: b.name }))}
                  selectedIds={form.levelRewardBadgeIds}
                  onAdd={(id) => setForm((f) => ({ ...f, levelRewardBadgeIds: [...f.levelRewardBadgeIds, id] }))}
                  onRemove={(id) => setForm((f) => ({ ...f, levelRewardBadgeIds: f.levelRewardBadgeIds.filter((x) => x !== id) }))}
                />
              </Box>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false })}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.level.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
